import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useRef, useState } from 'react';
import { Description, Dialog, DialogBackdrop, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Icon } from '../Icon';
import { createBreakpoint } from 'react-use';
import { motion } from 'framer-motion';
import classNames from 'classnames';
export const Modal = ({ children, isOpen, close, padding = true, title, description, size, autoScroll = true, zIndex = 30, persist = false, hideCloseButton = false, drag = false }) => {
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const ref = useRef(null);
    const containerRef = useRef(null);
    const breakpoint = createBreakpoint({ desktop: 1280, tablet: 768, mobile: 640 })();
    const isMobile = breakpoint === 'mobile';
    const getModalSize = () => {
        if (isMobile) {
            return '100%';
        }
        switch (size) {
            case 'sm':
                return '360px';
            case 'base':
                return '480px';
            case 'lg':
                return '600px';
            case 'xl':
                return '800px';
            default:
                return '480px';
        }
    };
    // @ts-ignore
    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientY);
    };
    const changeMarginBottom = (marginBottom) => {
        // @ts-ignore
        const content = ref.current?.parentElement;
        if (content) {
            content.style.transition = marginBottom ? 'all 0ms ease-in-out' : '';
            content.style.marginBottom = marginBottom || '';
        }
    };
    // @ts-ignore
    const handleTouchMove = (e) => {
        const touch = touchStart - e.targetTouches[0].clientY;
        if (touch < 0) {
            changeMarginBottom(touch + 'px');
            setTouchEnd(touch);
        }
    };
    // @ts-ignore
    const handleTouchEnd = () => {
        if (touchEnd < -50) {
            close?.();
        }
        changeMarginBottom();
    };
    const animation = 'ease-in-out duration-300';
    const titleClassName = classNames('font-bold text-gray-700 mb-2', size === 'xl' ? 'text-2xl' : 'text-xl');
    const descriptionClassName = 'text-base text-gray-700 mb-6';
    const contentClassName = autoScroll ? 'overflow-auto' : 'overflow-visible';
    const backdropClassName = 'fixed inset-0 bg-gray-700/30 duration-300 ease-out data-[closed]:opacity-0';
    const closeSwipeClassName = 'relative -mt-6 py-3 w-full after:absolute after:inset-0 after:m-auto after:bg-gray after:w-[100px] after:h-1.5 after:rounded-full md:hidden';
    const closeButtonClassName = 'z-50 absolute -right-3.5 -top-3.5 bg-gray-50 rounded-full border border-gray-200 text-gray p-[5px] outline-none';
    const panelClassName = classNames('inset-0 mx-auto flex flex-col bg-white h-fit rounded-t-xl shadow-xl md:rounded-xl ease-in-out duration-300 ', padding ? 'p-6' : 'pt-6 md:pt-0', !autoScroll ? 'my-auto relative' : 'm-auto mb-0 absolute max-h-[90vh] md:mb-auto', { 'mb-auto': !isMobile && autoScroll, 'cursor-grab active:cursor-grabbing': drag });
    const wrapperClasses = classNames('inset-0 fixed z-20', {
        'overflow-y-auto flex justify-center md:py-12': !autoScroll
    });
    const modalWidthStyles = { width: getModalSize() };
    const renderModalContent = () => (_jsxs(DialogPanel, { className: panelClassName, style: modalWidthStyles, children: [close && !persist && (_jsx("div", { className: closeSwipeClassName, ref: ref, onTouchEnd: handleTouchEnd, onTouchMove: handleTouchMove, onTouchStart: handleTouchStart })), title && _jsx(DialogTitle, { className: titleClassName, children: title }), description && _jsx(Description, { className: descriptionClassName, children: description }), _jsx("div", { className: contentClassName, children: children }), close && !persist && !isMobile && !hideCloseButton && (_jsx("button", { className: closeButtonClassName, onClick: close, children: _jsx(Icon, { name: "close" }) }))] }));
    return (_jsx(Transition, { as: Fragment, show: isOpen, children: _jsxs(Dialog, { className: wrapperClasses, initialFocus: ref, static: true, style: { zIndex }, onClose: (e) => {
                if (!persist) {
                    close?.(e);
                }
            }, ref: containerRef, children: [_jsx(TransitionChild, { as: Fragment, enter: animation, enterFrom: "opacity-0", enterTo: "opacity-100", leave: animation, leaveFrom: "opacity-100", leaveTo: "opacity-0", children: _jsx(DialogBackdrop, { className: backdropClassName }) }), _jsx(TransitionChild, { as: Fragment, enterFrom: "opacity-0 -mb-[100vh] md:scale-0", enterTo: "opacity-100 mb-0 md:m-auto md:scale-100", leaveFrom: "opacity-100 mb-0 md:m-auto md:scale-100", leaveTo: "opacity-0 -mb-[100vh] md:scale-0", children: drag && !isMobile ? (_jsx(motion.div, { drag: "x", dragConstraints: containerRef, className: "flex items-center", whileDrag: { scale: 1.02 }, dragMomentum: false, children: renderModalContent() })) : (renderModalContent()) })] }) }));
};
